import React from 'react';
import { Columns, RankBars } from './Charts';
import { flag, formatStay, fullDate, money, rate, sectionName, STEP_LABEL, when } from './format';

const PERIOD_LABEL = { '24h': '24 heures', '7j': '7 jours', '30j': '30 jours' };
const STEPS = ['visit', 'join', 'checkout', 'paid'];

function arrivalRows(insights, period) {
  if (period === '24h') {
    return (insights?.hourly || []).slice(-24).map((row) => {
      const label = new Date(row.hour).toLocaleTimeString('fr-FR', { hour: '2-digit', timeZone: 'Europe/Paris' });
      return { key: row.hour, label, value: row.visit, tip: `arrivés · ${label}` };
    });
  }
  const days = (insights?.daily || []).slice(period === '7j' ? -7 : -30);
  return days.map((row) => {
    const label = new Date(`${row.day}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    return { key: row.day, label, value: row.visit, tip: `arrivés · ${label}` };
  });
}

function Funnel({ funnel }) {
  const top = funnel?.visit || 0;
  return (
    <section className="ck-card ck-funnel">
      <header className="ck-card-head">
        <h2>Entonnoir</h2>
        <p>Visiteurs uniques. Hors toi, Nice et Antibes.</p>
      </header>
      <ol>
        {STEPS.map((step, index) => {
          const value = funnel?.[step] || 0;
          const prev = index ? funnel?.[STEPS[index - 1]] || 0 : 0;
          return (
            <li key={step}>
              {index > 0 && <span className="ck-funnel-rate">{rate(value, prev)}</span>}
              <b className={step === 'paid' && value ? 'ck-green' : ''}>{value}</b>
              <span>{STEP_LABEL[step]}</span>
              <i><s style={{ width: top ? `${Math.max(value ? 2 : 0, (value / top) * 100)}%` : 0 }} /></i>
            </li>
          );
        })}
      </ol>
      <p className="ck-foot">Arrivé → payé : <b>{rate(funnel?.paid || 0, top)}</b></p>
    </section>
  );
}

function Money({ money: byCurrency, stripe }) {
  const rows = Object.entries(byCurrency || {});
  return (
    <section className="ck-card">
      <header className="ck-card-head">
        <h2>Argent</h2>
        <p>Stripe, montant payé. Devises séparées.</p>
      </header>
      {!stripe && <p className="ck-empty">Stripe n’a pas répondu dans les 8 secondes.</p>}
      {stripe && rows.length === 0 && <p className="ck-empty">Aucun paiement réel sur la période.</p>}
      {rows.map(([currency, row]) => (
        <div key={currency} className="ck-money">
          <p className="ck-money-cur">{currency.toUpperCase()}</p>
          <div>
            <span>Encaissé</span>
            <b className="ck-green">{money(row.paid, currency)}</b>
            <small>{row.count} paiement{row.count > 1 ? 's' : ''}</small>
          </div>
          <div>
            <span>Remboursé</span>
            <b className={row.refunded ? 'ck-red' : ''}>{money(row.refunded, currency)}</b>
            <small>{row.refunds} remboursement{row.refunds > 1 ? 's' : ''}</small>
          </div>
          <div>
            <span>Net</span>
            <b>{money(row.paid - row.refunded, currency)}</b>
          </div>
        </div>
      ))}
      {stripe && (
        <p className="ck-foot">
          Sur les {stripe.scanned} dernières sessions Stripe complètes.
          {(stripe.tests || []).length > 0 && ` ${stripe.tests.length} test${stripe.tests.length > 1 ? 's' : ''} de Gauthier à part.`}
        </p>
      )}
    </section>
  );
}

function Durations({ stats }) {
  const d = stats?.durations;
  const rows = d ? [
    { key: 'lt10', label: '< 10 s', value: d.buckets.lt10, tip: 'visites de moins de 10 s' },
    { key: 's10', label: '10–30 s', value: d.buckets.s10_30, tip: 'visites de 10 à 30 s' },
    { key: 's30', label: '30 s–2 min', value: d.buckets.s30_120, tip: 'visites de 30 s à 2 min' },
    { key: 'gt2', label: '> 2 min', value: d.buckets.gt120, tip: 'visites de plus de 2 min' },
  ] : [];
  return (
    <section className="ck-card">
      <header className="ck-card-head">
        <h2>Temps passé</h2>
        <p>{d ? `Sur ${d.count} visite${d.count > 1 ? 's' : ''} mesurée${d.count > 1 ? 's' : ''}` : 'Aucune visite mesurée sur la période.'}</p>
      </header>
      {d && (
        <>
          <div className="ck-pair">
            <div><span>Moyenne</span><b className="ck-green">{formatStay(d.avgMs)}</b></div>
            <div><span>Médiane</span><b className="ck-green">{formatStay(d.medianMs)}</b></div>
          </div>
          <Columns rows={rows} height={120} />
        </>
      )}
    </section>
  );
}

function Live({ live, now, openPerson }) {
  return (
    <section className="ck-card ck-live">
      <header className="ck-card-head">
        <h2><span className="ck-dot is-live" /> En direct</h2>
        <p>{live.length ? `${live.length} sur le site` : 'Personne sur le site en ce moment.'}</p>
      </header>
      <ul>
        {live.map((person) => (
          <li key={person.sid || person.startedAt}>
            <button type="button" onClick={() => person.sid && openPerson(person.sid)}>
              <span className="ck-live-where">{flag(person.country)} {person.city || person.country || 'Lieu inconnu'}</span>
              <b className="ck-green">{formatStay(now - person.startedAt)}</b>
              <small>{person.section ? sectionName(person.section) : person.page}{person.video ? ` · vidéo ${person.video} %` : ''}</small>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Versions({ rows }) {
  return (
    <section className="ck-card ck-wide">
      <header className="ck-card-head">
        <h2>Entonnoir par version de page</h2>
        <p>Page d’arrivée. La racine / sert la V70, l’ancienne home est /v52.</p>
      </header>
      {rows.length === 0 ? <p className="ck-empty">Pas encore de visite sur la période.</p> : (
        <div className="ck-table-wrap">
          <table className="ck-table">
            <thead>
              <tr><th>Page</th><th>Arrivés</th><th>Clic rejoindre</th><th>Checkout</th><th>Payé</th><th>Conversion</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td><b>{row.key}</b>{row.visit < 30 && <em className="ck-note"> peu de volume</em>}</td>
                  <td>{row.visit}</td>
                  <td>{row.join} <small>{rate(row.join, row.visit)}</small></td>
                  <td>{row.checkout} <small>{rate(row.checkout, row.join)}</small></td>
                  <td className={row.paid ? 'ck-green' : ''}>{row.paid}</td>
                  <td><b>{rate(row.paid, row.visit)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Payments({ stripe }) {
  if (!stripe) return null;
  const line = (payment, test) => (
    <li key={`${test ? 't' : 'p'}-${payment.at}-${payment.email}`}>
      <span>{when(payment.at)}</span>
      <b className={test ? '' : 'ck-green'}>{money(payment.cents, payment.currency)}</b>
      <small>{payment.email}</small>
      {payment.refundedCents > 0 && <em className="ck-red">remboursé {money(payment.refundedCents, payment.currency)}{payment.refundedAt ? ` · ${when(payment.refundedAt)}` : ''}</em>}
    </li>
  );
  return (
    <section className="ck-card ck-wide ck-payments">
      <div>
        <h2>Paiements clients</h2>
        {(stripe.payments || []).length === 0 ? <p className="ck-empty">Aucun pour l’instant.</p> : <ul>{stripe.payments.slice(0, 12).map((payment) => line(payment, false))}</ul>}
      </div>
      <div>
        <h2>Tests de Gauthier · hors statistiques</h2>
        {(stripe.tests || []).length === 0 ? <p className="ck-empty">Aucun.</p> : <ul>{stripe.tests.slice(0, 12).map((payment) => line(payment, true))}</ul>}
        {(stripe.unmatched || []).length > 0 && (
          <>
            <h2>Remboursements hors liste</h2>
            <ul>
              {stripe.unmatched.map((refund) => (
                <li key={`${refund.at}-${refund.cents}`}><span>{when(refund.at)}</span><b className="ck-red">{money(refund.cents, refund.currency)}</b></li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

export default function Overview({ data, period, now, openPerson }) {
  const insight = data.insights?.periods?.[period] || {};
  const stats = data.cockpit?.periods?.[period] || {};
  const arrivals = arrivalRows(data.insights, period);
  const unique = (rows) => (rows || []).filter((row) => row.key && row.key !== 'inconnu');
  return (
    <div className="ck-view">
      <div className="ck-grid">
        <Funnel funnel={insight.funnel} />
        <Money money={stats.money} stripe={data.stripe} />
        <Durations stats={stats} />
        <section className="ck-card ck-span3">
          <header className="ck-card-head">
            <h2>Arrivées {period === '24h' ? 'par heure' : 'par jour'}</h2>
            <p>{PERIOD_LABEL[period]} · heure de Paris</p>
          </header>
          <Columns rows={arrivals} height={180} labelEvery={period === '24h' ? 3 : period === '7j' ? 1 : 5} empty="Pas encore d’arrivée sur la période." />
          {period === '24h' && data.traffic && (
            <p className="ck-foot">
              Cloudflare compte <b>{data.traffic.homepageVisits}</b> ouvertures de la page d’accueil sur 24 h, par de vrais navigateurs, sans ton IP ni les robots.
              Écart possible avec « arrivés » : Cloudflare ne compte que /, et « arrivés » ne voit ni ceux qui partent avant 1,5 s, ni les membres connectés, ni les navigateurs qui bloquent les scripts.
            </p>
          )}
        </section>
        <Live live={data.live || []} now={now} openPerson={openPerson} />
        <section className="ck-card ck-wide ck-origins">
          <header className="ck-card-head">
            <h2>D’où ils viennent</h2>
            <p>Arrivés sur la période</p>
          </header>
          <div>
            <h3>Pays</h3>
            <RankBars rows={unique(insight.countries).map((row) => ({ key: row.key, label: `${flag(row.key)} ${row.key}`, value: row.visit, note: row.paid ? `${row.paid} payé${row.paid > 1 ? 's' : ''}` : '' }))} />
          </div>
          <div>
            <h3>Villes</h3>
            <RankBars rows={unique(insight.cities).map((row) => ({ key: row.key, label: row.key, value: row.visit }))} />
          </div>
          <div>
            <h3>Sources</h3>
            <RankBars rows={(insight.sources || []).map((row) => ({ key: row.key, label: row.key === 'inconnu' ? 'Direct / inconnue' : row.key, value: row.visit }))} />
          </div>
          <div>
            <h3>Pages</h3>
            <RankBars rows={unique(insight.versions).map((row) => ({ key: row.key, label: row.key, value: row.visit, note: rate(row.paid, row.visit) }))} />
          </div>
        </section>
        <Versions rows={unique(insight.versions)} />
        <Payments stripe={data.stripe} />
      </div>
      <p className="ck-since">{data.since ? `Événements collectés depuis le ${fullDate(data.since)}.` : 'En attente de la première visite enregistrée.'}</p>
    </div>
  );
}
